import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  LinearProgress,
  Paper,
  Typography,
} from '@mui/material';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import ReplayIcon from '@mui/icons-material/Replay';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { motion, AnimatePresence } from 'framer-motion';
import type { BatchQueueItem, ZipProgress } from '../services/mediaContracts';
import type { VideoMetadata } from '../services/api';

const statusColor: Record<
  BatchQueueItem['status'],
  'default' | 'info' | 'success' | 'error' | 'warning'
> = {
  pending: 'default',
  fetching: 'info',
  ready: 'success',
  error: 'error',
  downloading: 'warning',
  done: 'success',
};

const statusLabel: Record<BatchQueueItem['status'], string> = {
  pending: 'Pending',
  fetching: 'Fetching',
  ready: 'Ready',
  error: 'Error',
  downloading: 'Saving',
  done: 'Done',
};

const shortUrl = (url: string): string => {
  const match = url.match(/(?:twitter|x)\.com\/([^/]+)\/status\/(\d+)/i);
  if (match) return `@${match[1]}/status/${match[2]}`;
  return url.replace(/^https?:\/\//, '');
};

interface BatchQueuePanelProps {
  items: BatchQueueItem[];
  isRunning: boolean;
  isZipping: boolean;
  zipProgress: ZipProgress | null;
  readyCount: number;
  errorCount: number;
  pendingCount: number;
  onDownloadZip: () => void;
  onClear: () => void;
  onRetryFailed: () => void;
  onRemove: (url: string) => void;
  onItemReady?: (info: VideoMetadata) => void;
  message?: string | null;
  messageSeverity?: 'success' | 'warning' | 'error' | 'info';
  onDismissMessage?: () => void;
}

const BatchQueuePanel: React.FC<BatchQueuePanelProps> = ({
  items,
  isRunning,
  isZipping,
  zipProgress,
  readyCount,
  errorCount,
  pendingCount,
  onDownloadZip,
  onClear,
  onRetryFailed,
  onRemove,
  onItemReady,
  message,
  messageSeverity = 'info',
  onDismissMessage,
}) => {
  if (items.length === 0) return null;

  const busy = isRunning || isZipping;
  const doneCount = items.filter((i) => i.status === 'done').length;
  const fetchedCount = items.length - pendingCount;
  const fetchProgress =
    items.length > 0 ? (fetchedCount / items.length) * 100 : 0;

  return (
    <Paper
      elevation={0}
      component={motion.div}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      sx={{
        p: 2,
        backgroundColor: '#16181C',
        border: '1px solid #2F3336',
        borderRadius: 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          mb: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Batch queue ({items.length})
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {readyCount} ready
            {doneCount > 0 ? ` · ${doneCount} done` : ''}
            {errorCount > 0 ? ` · ${errorCount} failed` : ''}
            {pendingCount > 0 ? ` · ${pendingCount} left` : ''}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {errorCount > 0 && (
            <Button
              size="small"
              startIcon={<ReplayIcon />}
              onClick={onRetryFailed}
              disabled={busy}
              aria-label="Retry failed posts"
              sx={{ textTransform: 'none' }}
            >
              Retry failed
            </Button>
          )}
          <Button
            size="small"
            startIcon={<ClearAllIcon />}
            onClick={onClear}
            disabled={busy}
            aria-label="Clear batch queue"
            sx={{ textTransform: 'none', color: 'text.secondary' }}
          >
            Clear
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={
              isZipping ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <CloudDownloadIcon />
              )
            }
            onClick={onDownloadZip}
            disabled={busy || readyCount === 0}
            aria-label={`Download ${readyCount} ready posts as ZIP`}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {`Download all as ZIP (${readyCount})`}
          </Button>
        </Box>
      </Box>

      {isRunning && (
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mb: 0.5, display: 'block' }}
          >
            Fetching posts… {fetchedCount} / {items.length}
          </Typography>
          <LinearProgress variant="determinate" value={fetchProgress} />
        </Box>
      )}

      <AnimatePresence>
        {zipProgress && (
          <Box
            component={motion.div}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            sx={{ mb: 2, overflow: 'hidden' }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mb: 0.5, display: 'block' }}
            >
              Packing {zipProgress.current}/{zipProgress.total}
              {zipProgress.label ? ` · ${zipProgress.label}` : ''}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={
                zipProgress.total
                  ? (zipProgress.current / zipProgress.total) * 100
                  : 0
              }
            />
          </Box>
        )}
      </AnimatePresence>

      {message && (
        <Alert
          severity={messageSeverity}
          onClose={onDismissMessage}
          sx={{ mb: 2 }}
        >
          {message}
        </Alert>
      )}

      {!busy && readyCount === 0 && errorCount > 0 && items.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          No posts are ready. Retry failed items or clear the queue.
        </Alert>
      )}

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          maxHeight: 360,
          overflowY: 'auto',
          pr: 0.5,
        }}
      >
        <AnimatePresence initial={false}>
          {items.map((item) => {
            const thumb =
              item.metadata?.thumbnail ||
              item.metadata?.images?.[0]?.url ||
              item.metadata?.videos?.[0]?.thumbnail;
            const canOpen =
              Boolean(item.metadata) &&
              (item.status === 'ready' || item.status === 'done') &&
              Boolean(onItemReady);

            return (
              <Box
                key={item.url}
                component={motion.div}
                layout
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.25 }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  py: 1,
                  px: 1.5,
                  borderRadius: 1,
                  backgroundColor: '#0F1419',
                  border: '1px solid #2F3336',
                  cursor: canOpen ? 'pointer' : 'default',
                  transition: 'border-color 160ms ease',
                  '&:hover': canOpen
                    ? { borderColor: '#1D9BF0' }
                    : undefined,
                }}
                onClick={() => {
                  if (canOpen && item.metadata && onItemReady) {
                    onItemReady(item.metadata);
                  }
                }}
              >
                {thumb ? (
                  <Box
                    component="img"
                    src={thumb}
                    alt=""
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 1,
                      objectFit: 'cover',
                      flexShrink: 0,
                      backgroundColor: '#202327',
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 1,
                      flexShrink: 0,
                      backgroundColor: '#202327',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {item.status === 'fetching' ? (
                      <CircularProgress size={16} />
                    ) : null}
                  </Box>
                )}

                <Chip
                  size="small"
                  label={statusLabel[item.status]}
                  color={statusColor[item.status]}
                  sx={{ minWidth: 72, flexShrink: 0 }}
                />

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    noWrap
                    sx={{
                      color: '#E7E9EA',
                      fontFamily: 'monospace',
                      fontSize: '0.8rem',
                    }}
                  >
                    {shortUrl(item.url)}
                  </Typography>
                  {item.error && (
                    <Typography variant="caption" color="error" noWrap display="block">
                      {item.error}
                    </Typography>
                  )}
                  {item.metadata && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      noWrap
                      display="block"
                    >
                      {item.metadata.author || 'Unknown'}
                      {(item.metadata.videos.length +
                        (item.metadata.images?.length || 0)) >
                        0 &&
                        ` · ${
                          item.metadata.videos.length +
                          (item.metadata.images?.length || 0)
                        } media`}
                      {item.metadata.title
                        ? ` · ${item.metadata.title.slice(0, 48)}`
                        : ''}
                    </Typography>
                  )}
                </Box>

                {canOpen && (
                  <IconButton
                    size="small"
                    aria-label="Open in viewer"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (item.metadata && onItemReady) {
                        onItemReady(item.metadata);
                      }
                    }}
                    sx={{ color: 'text.secondary' }}
                  >
                    <OpenInNewIcon fontSize="small" />
                  </IconButton>
                )}

                <IconButton
                  size="small"
                  aria-label="Remove from queue"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemove(item.url);
                  }}
                  disabled={busy && item.status === 'fetching'}
                  sx={{ color: 'text.secondary' }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            );
          })}
        </AnimatePresence>
      </Box>
    </Paper>
  );
};

export default BatchQueuePanel;
